import { z } from 'zod';
import {
  buscarClienteInternoQuerySchema,
  confirmarCadastroInternoSchema,
  prepararCadastroSchema,
} from './cadastro.js';
import { STATUS_VOUCHER } from './campanha.js';

/**
 * Contrato das 5 tools MCP do Hermes (SPEC §9). Este arquivo é a **fronteira
 * estável** entre a `api` (que serve o MCP fino em `/mcp` sobre `/interno/*`) e o
 * agente Hermes. O agente **não edita este arquivo** — mudança de contrato passa
 * por aqui, no lado da API, para não haver conflito de merge.
 *
 * As tools falam JSON snake_case (convenção MCP); os schemas Zod aqui são os
 * mesmos da API (camelCase) — o MCP server faz o de-para 1:1. Nenhuma regra de
 * negócio: só formato. Telefone/documento seguem crus (normalizados na API).
 */

// ── buscar_cliente ──────────────────────────────────────────────────────────

export const buscarClienteEntradaSchema = z
  .object({
    telefone: z.string().trim().min(1).optional(),
    nome: z.string().trim().min(1).optional(),
  })
  .refine((o) => o.telefone ?? o.nome, { message: 'Informe telefone ou nome' });

export const clienteResumoSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  telefone: z.string(),
  documento: z.string().nullable(),
  codigoErp: z.string().nullable(),
  jaEraClienteVarga: z.boolean(),
  ativo: z.boolean(),
  codigo: z.string().nullable(),
});
export const buscarClienteSaidaSchema = z.object({ cliente: clienteResumoSchema.nullable() });

// ── preparar_cadastro ───────────────────────────────────────────────────────

export const prepararCadastroEntradaSchema = prepararCadastroSchema;

export const prepararCadastroSaidaSchema = z.object({
  rascunhoId: z.string().uuid(),
  dados: z.unknown(),
  avisos: z.array(z.string()),
});

// ── confirmar_cadastro ──────────────────────────────────────────────────────

export const confirmarCadastroEntradaSchema = confirmarCadastroInternoSchema;

export const voucherResumoSchema = z.object({
  papel: z.enum(['indicador', 'indicado']),
  voucherCodigo: z.string(),
  descricao: z.string(),
});

export const confirmarCadastroSaidaSchema = z.object({
  idempotente: z.boolean(),
  cliente: z.object({
    id: z.string().uuid(),
    nome: z.string(),
    telefone: z.string(),
    origem: z.string(),
    jaEraClienteVarga: z.boolean(),
    novo: z.boolean(),
  }),
  codigo: z.string(),
  linkWa: z.string(),
  notificacaoId: z.string(),
  linkPrimeiroAcesso: z.string().nullable(),
  indicacao: z
    .object({
      id: z.string().uuid(),
      status: z.enum(['convertida', 'rejeitada']),
      motivo: z.string().optional(),
      nivelIndicador: z.number().int().optional(),
    })
    .nullable(),
  vouchers: z.array(voucherResumoSchema),
});

// ── consultar_vouchers ──────────────────────────────────────────────────────

export const consultarVouchersEntradaSchema = z
  .object({
    telefone: z.string().trim().min(1).optional(),
    documento: z.string().trim().min(1).optional(),
    codigo: z.string().trim().min(1).optional(),
  })
  .refine((o) => o.telefone ?? o.documento ?? o.codigo, {
    message: 'Informe telefone, documento ou código',
  });

export const voucherDetalheSchema = z.object({
  id: z.string().uuid(),
  voucherCodigo: z.string(),
  clienteId: z.string().uuid(),
  clienteNome: z.string(),
  papel: z.enum(['indicador', 'indicado']).nullable(),
  nivel: z.number().int().nullable(),
  descricao: z.string(),
  status: z.enum(STATUS_VOUCHER),
  concedidoEm: z.string(),
  entregueEm: z.string().nullable(),
  entreguePara: z.string().nullable(),
});

export const consultarVouchersSaidaSchema = z.object({
  cliente: clienteResumoSchema.nullable(),
  vouchers: z.array(voucherDetalheSchema),
  resumo: z.object({
    total: z.number().int(),
    disponiveis: z.number().int(),
    entregues: z.number().int(),
  }),
});

// ── listar_pendentes ────────────────────────────────────────────────────────

export const listarPendentesEntradaSchema = z.object({});

export const notificacaoPendenteSchema = z.object({
  id: z.string().uuid(),
  clienteId: z.string().uuid(),
  clienteNome: z.string(),
  telefone: z.string(),
  tipo: z.enum(['boas_vindas', 'indicacao_convertida', 'recuperacao_senha', 'quase_la']),
  texto: z.string(),
  linkWa: z.string(),
  criadoEm: z.string(),
});

export const listarPendentesSaidaSchema = z.object({
  itens: z.array(notificacaoPendenteSchema),
});

// ── índice das tools ────────────────────────────────────────────────────────

export const HERMES_TOOLS = {
  buscar_cliente: {
    rota: 'GET /v1/interno/cliente/buscar',
    entrada: buscarClienteInternoQuerySchema,
    saida: buscarClienteSaidaSchema,
  },
  preparar_cadastro: {
    rota: 'POST /v1/interno/cadastro/preparar',
    entrada: prepararCadastroEntradaSchema,
    saida: prepararCadastroSaidaSchema,
  },
  confirmar_cadastro: {
    rota: 'POST /v1/interno/cadastro/confirmar',
    entrada: confirmarCadastroEntradaSchema,
    saida: confirmarCadastroSaidaSchema,
  },
  consultar_vouchers: {
    rota: 'GET /v1/interno/cliente/vouchers',
    entrada: consultarVouchersEntradaSchema,
    saida: consultarVouchersSaidaSchema,
  },
  listar_pendentes: {
    rota: 'GET /v1/interno/notificacoes',
    entrada: listarPendentesEntradaSchema,
    saida: listarPendentesSaidaSchema,
  },
} as const;

export type HermesToolNome = keyof typeof HERMES_TOOLS;
